import { useState, useEffect } from 'react';
import { openGlobalOverlay, closeGlobalOverlay } from '../util/globalOverlays.js';
import { Modal, Button } from './ui';
import { useAuth } from '../auth/AuthContext.jsx';

// T139 — optional, one-shot, replayable first-run tour over the T129 dashboard
// zones. Prose-Modal variant (no spotlight cut-out): reuses the shared Modal
// (focus trap + Escape + scrim from T131). The seen-flag lives in localStorage
// under the hunter.tour.* key, mirroring the hunter.onboarding.v1.<id>
// convention. OnboardingPage.finish() pre-sets this key so a user who just
// completed the wizard never gets the tour stacked on top of it.
// T290b — this was a single global key, so on a browser with more than one
// account only the FIRST person ever saw the tour: everyone after inherited a
// "seen" flag they had never seen anything to earn. The comment above already
// says it mirrors `hunter.onboarding.v1.<id>` — that convention carries the id
// and this did not. Kept as a bare constant too, because OnboardingPage and the
// existing tests reference it, and a user-less call must still mean something.
export const TOUR_KEY = 'hunter.tour.dashboard.v1';

export const tourKeyFor = (user) => (user?.id ? `${TOUR_KEY}.${user.id}` : TOUR_KEY);

const STEPS = [
  {
    eyebrow: 'Tour · 1 of 3',
    title: 'Today',
    body: 'The top zone is your daily picture: scan status, the morning brief, and what you are hunting for. Hit "Scan now" to refresh matches.',
  },
  {
    eyebrow: 'Tour · 2 of 3',
    title: 'Work queue',
    body: 'Score-ranked jobs you have not acted on yet. Hover any score or stage pill for a breakdown, or press ? for keyboard shortcuts. Click a row to open the full job drawer.',
  },
  {
    eyebrow: 'Tour · 3 of 3',
    title: 'System',
    body: 'Quiet unless something needs a look: coverage stats and per-source health. The small "?" icons across the app explain anything dense — try the one by "Source health".',
  },
];

export function shouldAutoShowTour(user) {
  // Don't auto-run without a logged-in user, and never repeat once seen.
  // Replay is always allowed via the manual entry point (forceOpen).
  if (!user) return false;
  try {
    // T290c — no legacy fallback on purpose, and the earlier comment here
    // claimed one it never implemented. Honouring the old un-suffixed key would
    // hand its "seen" state to EVERY account on the browser, which is the bug
    // being fixed. The cost of not honouring it is that whoever already
    // dismissed the tour sees it once more and presses Skip.
    if (localStorage.getItem(tourKeyFor(user))) return false;
  } catch {
    return false;
  }
  return true;
}

export default function DashboardTour({ forceOpen = false, onClose }) {
  const { user } = useAuth();
  const [i, setI] = useState(0);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (forceOpen) {
      setI(0);
      setOpen(true);
      return;
    }
    if (shouldAutoShowTour(user)) setOpen(true);
  }, [forceOpen, user]);

  // T290c — announce ourselves so the page can disarm its triage shortcuts. The
  // tour is a first-run overlay over the very list those keys act on.
  useEffect(() => {
    if (!open) return undefined;
    openGlobalOverlay('dashboard-tour');
    return () => closeGlobalOverlay('dashboard-tour');
  }, [open]);

  const finish = () => {
    try {
      localStorage.setItem(tourKeyFor(user), '1');
    } catch {
      /* private mode — the tour just shows again next reload, harmless */
    }
    setOpen(false);
    onClose?.();
  };

  if (!open) return null;
  const step = STEPS[i];
  const last = i === STEPS.length - 1;

  return (
    <Modal
      open
      onClose={finish}
      size="sm"
      placement="center"
      eyebrow={step.eyebrow}
      title={step.title}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={finish}>
            Skip
          </Button>
          <span className="flex-1" />
          {i > 0 && (
            <Button size="sm" onClick={() => setI((n) => n - 1)}>
              Back
            </Button>
          )}
          <Button variant="primary" size="sm" onClick={() => (last ? finish() : setI((n) => n + 1))}>
            {last ? 'Done' : 'Next'}
          </Button>
        </>
      }
    >
      <div className="p-4 text-sm leading-relaxed text-slate-300">{step.body}</div>
    </Modal>
  );
}
